// FieldVision Admin - User Detail Component v1.0
// Viser og redigerer brugerdetaljer med kvota, CVR'er og password funktioner

import React, { useState, useEffect } from 'react';
import {
  getUserById,
  updateUser,
  resetUserQuota,
  sendPasswordResetEmail,
  setTempPassword as apiSetTempPassword,
  getUserFields,
  uploadUserMaps,
  deleteUser,
  getCurrentUser,
  User,
  UserQuota,
  UserField,
  MapUploadResult,
} from '../services/api';

// Tier konfiguration
const TIER_OPTIONS = ['Basis', 'Pro', 'Advanced'];
const TIER_LIMITS: Record<string, number> = {
  Basis: 15,
  Pro: 50,
  Advanced: 999,
};

interface UserDetailProps {
  userId: number;
  onBack: () => void;
}

const UserDetail: React.FC<UserDetailProps> = ({ userId, onBack }) => {
  // Data states
  const [user, setUser] = useState<User | null>(null);
  const [cvrs, setCvrs] = useState<string[]>([]);
  const [quota, setQuota] = useState<UserQuota | null>(null);

  // Form states
  const [formData, setFormData] = useState<Partial<User>>({});
  const [hasChanges, setHasChanges] = useState(false);

  // UI states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Password modal states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [passwordCopied, setPasswordCopied] = useState(false);

  // Delete confirmation states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Upload states
  const [selectedCvr, setSelectedCvr] = useState<string>('');
  const [fields, setFields] = useState<UserField[]>([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState<MapUploadResult[]>([]);

  // Load user data
  const loadUser = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getUserById(userId);
      setUser(data.user);
      setCvrs(data.cvrs);
      setQuota(data.quota);
      setFormData({
        Name: data.user.Name || '',
        Phone: data.user.Phone || '',
        CompanyName: data.user.CompanyName || '',
        Address: data.user.Address || '',
        PostalCode: data.user.PostalCode || '',
        City: data.user.City || '',
        CVR: data.user.CVR || '',
        SubscriptionTier: data.user.SubscriptionTier,
        IsActive: data.user.IsActive,
      });
      setHasChanges(false);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
  }, [userId]);

  // Delete user handler
  const handleDeleteUser = async () => {
    setDeleting(true);
    setError('');
    try {
      await deleteUser(userId);
      setShowDeleteModal(false);
      // Naviger tilbage til listen efter sletning
      onBack();
    } catch (err: any) {
      setError(err.message);
      setDeleting(false);
    }
  };

  // Check om current user er denne bruger (kan ikke slette sig selv)
  const currentUser = getCurrentUser();
  const isSelf = currentUser?.userId === userId;

  // Handle form changes
  const handleChange = (field: keyof User, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  // Save changes
  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await updateUser(userId, formData);
      setSuccess('Bruger opdateret!');
      setHasChanges(false);
      loadUser(); // Refresh
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Reset quota
  const handleResetQuota = async () => {
    if (!window.confirm('Nulstil brugerens kvota for denne maaned?')) return;

    setError('');
    setSuccess('');
    try {
      const result = await resetUserQuota(userId);
      setSuccess(`Kvota nulstillet (${result.previousCount} -> ${result.newCount})`);
      loadUser(); // Refresh
    } catch (err: any) {
      setError(err.message);
    }
  };

  // Send password reset email
  const handleSendResetEmail = async () => {
    if (!window.confirm('Send password reset email til brugeren?')) return;

    setError('');
    setSuccess('');
    try {
      await sendPasswordResetEmail(userId);
      setSuccess('Password reset email sendt!');
    } catch (err: any) {
      setError(err.message);
    }
  };

  // Generate temp password
  const handleSetTempPassword = async () => {
    setError('');
    try {
      const result = await apiSetTempPassword(userId);
      setTempPassword(result.tempPassword);
      setShowPasswordModal(true);
      setPasswordCopied(false);
    } catch (err: any) {
      setError(err.message);
    }
  };

  // Copy password to clipboard
  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(tempPassword);
      setPasswordCopied(true);
      setTimeout(() => setPasswordCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = tempPassword;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setPasswordCopied(true);
      setTimeout(() => setPasswordCopied(false), 2000);
    }
  };

  // Handle CVR selection for upload
  const handleCvrChange = async (cvr: string) => {
    setSelectedCvr(cvr);
    setFields([]);
    setSelectedFiles([]);
    setUploadResults([]);

    if (!cvr) return;

    setLoadingFields(true);
    try {
      const data = await getUserFields(userId, cvr);
      setFields(data.fields);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoadingFields(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      setSelectedFiles(Array.from(files));
      setUploadResults([]);
    }
  };

  // Handle upload
  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setError('');
    try {
      const result = await uploadUserMaps(userId, selectedCvr, selectedFiles);
      setUploadResults(result.results);
      setSuccess(result.message);
      setSelectedFiles([]);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  // Format date
  const formatDateTime = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('da-DK', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Quota progress percentage
  const getQuotaPercentage = (): number => {
    if (!quota) return 0;
    return Math.min((quota.used / quota.limit) * 100, 100);
  };

  // Quota progress color
  const getQuotaColor = (): string => {
    const percentage = getQuotaPercentage();
    if (percentage >= 80) return '#dc2626';
    if (percentage >= 50) return '#ca8a04';
    return '#16a34a';
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Henter bruger...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>Kunne ikke hente bruger</div>
        <button onClick={onBack} style={styles.backButton}>
          &larr; Tilbage
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button onClick={onBack} style={styles.backButton}>
          &larr; Tilbage til brugere
        </button>
        <h2 style={styles.title}>{user.Email}</h2>
        <div style={styles.headerRight}>
          {user.IsAdmin && <span style={styles.adminBadge}>Admin</span>}
        </div>
      </div>

      {/* Alerts */}
      {error && <div style={styles.alert}>{error}</div>}
      {success && <div style={styles.successAlert}>{success}</div>}

      {/* Content Grid */}
      <div style={styles.grid}>
        {/* Brugeroplysninger */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Brugeroplysninger</h3>

          <div style={styles.formGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="text"
              value={user.Email}
              disabled
              style={{ ...styles.input, ...styles.inputDisabled }}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Navn</label>
            <input
              type="text"
              value={formData.Name || ''}
              onChange={(e) => handleChange('Name', e.target.value)}
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Telefon</label>
            <input
              type="text"
              value={formData.Phone || ''}
              onChange={(e) => handleChange('Phone', e.target.value)}
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Firma</label>
            <input
              type="text"
              value={formData.CompanyName || ''}
              onChange={(e) => handleChange('CompanyName', e.target.value)}
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Adresse</label>
            <input
              type="text"
              value={formData.Address || ''}
              onChange={(e) => handleChange('Address', e.target.value)}
              style={styles.input}
            />
          </div>

          <div style={styles.formRow}>
            <div style={{ ...styles.formGroup, flex: 1 }}>
              <label style={styles.label}>Postnr.</label>
              <input
                type="text"
                value={formData.PostalCode || ''}
                onChange={(e) => handleChange('PostalCode', e.target.value)}
                style={styles.input}
              />
            </div>
            <div style={{ ...styles.formGroup, flex: 2 }}>
              <label style={styles.label}>By</label>
              <input
                type="text"
                value={formData.City || ''}
                onChange={(e) => handleChange('City', e.target.value)}
                style={styles.input}
              />
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>CVR</label>
            <input
              type="text"
              value={formData.CVR || ''}
              onChange={(e) => handleChange('CVR', e.target.value)}
              style={styles.input}
            />
          </div>

          {/* Active toggle */}
          <div style={styles.toggleRow}>
            <span style={styles.toggleLabel}>Bruger aktiv</span>
            <button
              onClick={() => handleChange('IsActive', !formData.IsActive)}
              style={{
                ...styles.toggle,
                backgroundColor: formData.IsActive ? '#16a34a' : '#d1d5db',
              }}
            >
              <span
                style={{
                  ...styles.toggleKnob,
                  transform: formData.IsActive ? 'translateX(20px)' : 'translateX(0)',
                }}
              />
            </button>
          </div>
        </div>

        {/* Right column */}
        <div style={styles.rightColumn}>
          {/* CVR'er tilknyttet */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>CVR'er tilknyttet ({cvrs.length})</h3>
            {cvrs.length === 0 ? (
              <div style={styles.emptyText}>Ingen CVR'er tilknyttet</div>
            ) : (
              <div style={styles.cvrList}>
                {cvrs.map((cvr) => (
                  <div key={cvr} style={styles.cvrItem}>
                    {cvr}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Abonnement */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Abonnement</h3>

            <div style={styles.formGroup}>
              <label style={styles.label}>Tier</label>
              <select
                value={formData.SubscriptionTier || 'Basis'}
                onChange={(e) => handleChange('SubscriptionTier', e.target.value)}
                style={styles.select}
              >
                {TIER_OPTIONS.map((tier) => (
                  <option key={tier} value={tier}>
                    {tier} ({TIER_LIMITS[tier]} kort/md)
                  </option>
                ))}
              </select>
            </div>

            {/* Kvota progress */}
            {quota && (
              <div style={styles.quotaSection}>
                <div style={styles.quotaHeader}>
                  <span style={styles.quotaLabel}>Kvota {quota.yearMonth}</span>
                  <span style={{ color: getQuotaColor(), fontWeight: 600 }}>
                    {quota.used} / {quota.limit}
                  </span>
                </div>
                <div style={styles.progressBar}>
                  <div
                    style={{
                      ...styles.progressFill,
                      width: `${getQuotaPercentage()}%`,
                      backgroundColor: getQuotaColor(),
                    }}
                  />
                </div>
                <button onClick={handleResetQuota} style={styles.resetButton}>
                  Nulstil kvota
                </button>
              </div>
            )}
          </div>

          {/* Password */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Password</h3>
            <div style={styles.buttonGroup}>
              <button onClick={handleSendResetEmail} style={styles.secondaryButton}>
                Send reset-link
              </button>
              <button onClick={handleSetTempPassword} style={styles.secondaryButton}>
                Generer midlertidigt password
              </button>
            </div>
          </div>

          {/* System info */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>System</h3>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Oprettet</span>
              <span style={styles.infoValue}>{formatDateTime(user.CreatedAt)}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Sidst logget ind</span>
              <span style={styles.infoValue}>{formatDateTime(user.LastLogin)}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>User ID</span>
              <span style={styles.infoValue}>{user.UserId}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Upload tildelingskort section */}
      {cvrs.length > 0 && (
        <div style={{ ...styles.card, marginBottom: '24px' }}>
          <h3 style={styles.cardTitle}>Upload tildelingskort</h3>

          {/* CVR dropdown */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Vælg CVR</label>
            <select
              value={selectedCvr}
              onChange={(e) => handleCvrChange(e.target.value)}
              style={styles.select}
            >
              <option value="">Vælg CVR...</option>
              {cvrs.map((cvr) => (
                <option key={cvr} value={cvr}>
                  {cvr}
                </option>
              ))}
            </select>
          </div>

          {/* Fields preview */}
          {loadingFields && (
            <div style={styles.emptyText}>Henter marker...</div>
          )}

          {selectedCvr && fields.length > 0 && (
            <div style={styles.fieldsPreview}>
              <div style={styles.fieldsHeader}>
                {fields.length} marker tilgængelige
              </div>
              <div style={styles.fieldsList}>
                {fields.slice(0, 10).map((field) => (
                  <span key={field.marknr} style={styles.fieldTag}>
                    {field.marknr}
                  </span>
                ))}
                {fields.length > 10 && (
                  <span style={styles.fieldTag}>+{fields.length - 10} mere</span>
                )}
              </div>
            </div>
          )}

          {/* File input */}
          {selectedCvr && fields.length > 0 && (
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Vælg filer (ZIP shapefiles)
              </label>
              <input
                type="file"
                accept=".zip"
                multiple
                onChange={handleFileSelect}
                style={styles.fileInput}
              />
              <p style={styles.helpText}>
                Filnavn format: Marknr_MapType_Terminal.zip (f.eks. 11-6_NDVI_JohnDeere.zip)
              </p>
            </div>
          )}

          {/* Selected files preview */}
          {selectedFiles.length > 0 && (
            <div style={styles.selectedFiles}>
              <div style={styles.fieldsHeader}>
                {selectedFiles.length} fil(er) valgt:
              </div>
              {selectedFiles.map((file, idx) => (
                <div key={idx} style={styles.fileName}>
                  {file.name}
                </div>
              ))}
              <button
                onClick={handleUpload}
                disabled={uploading}
                style={{
                  ...styles.primaryButton,
                  marginTop: '12px',
                  opacity: uploading ? 0.5 : 1,
                }}
              >
                {uploading ? 'Uploader...' : 'Upload filer'}
              </button>
            </div>
          )}

          {/* Upload results */}
          {uploadResults.length > 0 && (
            <div style={styles.uploadResults}>
              <div style={styles.fieldsHeader}>Resultat:</div>
              {uploadResults.map((result, idx) => (
                <div
                  key={idx}
                  style={{
                    ...styles.resultRow,
                    backgroundColor: result.success ? '#f0fdf4' : '#fef2f2',
                  }}
                >
                  <span style={styles.resultFilename}>{result.filename}</span>
                  {result.success ? (
                    <span style={{ color: '#16a34a' }}>
                      OK - {result.marknr} ({result.mapType})
                    </span>
                  ) : (
                    <span style={{ color: '#dc2626' }}>{result.error}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Farezone - Slet bruger */}
      <div style={styles.dangerZone}>
        <h4 style={styles.dangerZoneTitle}>Farezone</h4>
        <div style={styles.dangerZoneContent}>
          <div style={styles.dangerZoneText}>
            <strong>Slet bruger permanent</strong>
            <p style={styles.dangerZoneDescription}>
              Sletter brugeren og al relateret data (ordrer, CVR-tilknytninger, kvota).
              Denne handling kan ikke fortrydes.
            </p>
          </div>
          <button
            onClick={() => setShowDeleteModal(true)}
            disabled={user?.IsAdmin || isSelf}
            style={{
              ...styles.dangerButton,
              opacity: user?.IsAdmin || isSelf ? 0.5 : 1,
              cursor: user?.IsAdmin || isSelf ? 'not-allowed' : 'pointer',
            }}
            title={
              user?.IsAdmin
                ? 'Admin brugere kan ikke slettes'
                : isSelf
                ? 'Du kan ikke slette din egen konto'
                : 'Slet bruger'
            }
          >
            Slet bruger
          </button>
        </div>
        {user?.IsAdmin && (
          <p style={styles.dangerZoneWarning}>
            Admin brugere kan ikke slettes fra admin portalen.
          </p>
        )}
        {isSelf && !user?.IsAdmin && (
          <p style={styles.dangerZoneWarning}>
            Du kan ikke slette din egen konto.
          </p>
        )}
      </div>

      {/* Save button */}
      <div style={styles.saveSection}>
        <button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          style={{
            ...styles.primaryButton,
            opacity: !hasChanges || saving ? 0.5 : 1,
          }}
        >
          {saving ? 'Gemmer...' : 'Gem aendringer'}
        </button>
        {hasChanges && (
          <span style={styles.unsavedText}>Du har ugemte aendringer</span>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.deleteModal}>
            <div style={styles.deleteModalIcon}>!</div>
            <h3 style={styles.deleteModalTitle}>Bekraeft sletning</h3>
            <p style={styles.deleteModalText}>
              Er du sikker paa at du vil slette brugeren
            </p>
            <p style={styles.deleteModalEmail}>{user?.Email}</p>
            <p style={styles.deleteModalWarning}>
              Dette vil permanent slette alle brugerens data inklusiv ordrer,
              CVR-tilknytninger og kvota. Handlingen kan ikke fortrydes.
            </p>
            {error && <div style={styles.deleteModalError}>{error}</div>}
            <div style={styles.deleteModalButtons}>
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                style={styles.cancelButton}
              >
                Annuller
              </button>
              <button
                onClick={handleDeleteUser}
                disabled={deleting}
                style={{
                  ...styles.confirmDeleteButton,
                  opacity: deleting ? 0.7 : 1,
                }}
              >
                {deleting ? 'Sletter...' : 'Ja, slet bruger'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Modal */}
      {showPasswordModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Midlertidigt password</h3>
            <p style={styles.modalText}>
              Brugerens password er sat til:
            </p>
            <div style={styles.passwordBox}>
              <code style={styles.passwordText}>{tempPassword}</code>
            </div>
            <div style={styles.modalButtons}>
              <button
                onClick={handleCopyPassword}
                style={{
                  ...styles.copyButton,
                  backgroundColor: passwordCopied ? '#16a34a' : '#2563eb',
                }}
              >
                {passwordCopied ? 'Kopieret!' : 'Kopier password'}
              </button>
              <button
                onClick={() => setShowPasswordModal(false)}
                style={styles.closeButton}
              >
                Luk
              </button>
            </div>
            <p style={styles.warningText}>
              Giv dette password til brugeren. De skal skifte det ved naeste login.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
    maxWidth: '1000px',
    margin: '0 auto',
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: '#6b7280',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
  },
  backButton: {
    padding: '8px 16px',
    fontSize: '14px',
    color: '#6b7280',
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 600,
    flex: 1,
  },
  headerRight: {
    display: 'flex',
    gap: '8px',
  },
  adminBadge: {
    padding: '4px 12px',
    fontSize: '12px',
    fontWeight: 600,
    color: '#9333ea',
    backgroundColor: '#f3e8ff',
    borderRadius: '12px',
  },
  alert: {
    padding: '12px 16px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    color: '#dc2626',
    marginBottom: '16px',
  },
  successAlert: {
    padding: '12px 16px',
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '8px',
    color: '#16a34a',
    marginBottom: '16px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
    marginBottom: '24px',
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  cardTitle: {
    margin: '0 0 16px 0',
    fontSize: '14px',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
  },
  formGroup: {
    marginBottom: '16px',
  },
  formRow: {
    display: 'flex',
    gap: '12px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 500,
    color: '#374151',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  inputDisabled: {
    backgroundColor: '#f3f4f6',
    color: '#6b7280',
    cursor: 'not-allowed',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    outline: 'none',
    boxSizing: 'border-box',
    backgroundColor: 'white',
  },
  toggleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 0',
    borderTop: '1px solid #f3f4f6',
    marginTop: '8px',
  },
  toggleLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  },
  toggle: {
    width: '48px',
    height: '28px',
    borderRadius: '14px',
    border: 'none',
    cursor: 'pointer',
    position: 'relative',
    transition: 'background-color 0.2s',
  },
  toggleKnob: {
    position: 'absolute',
    top: '4px',
    left: '4px',
    width: '20px',
    height: '20px',
    backgroundColor: 'white',
    borderRadius: '50%',
    transition: 'transform 0.2s',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
  },
  emptyText: {
    color: '#9ca3af',
    fontSize: '14px',
    fontStyle: 'italic',
  },
  cvrList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  cvrItem: {
    padding: '6px 12px',
    backgroundColor: '#f3f4f6',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'monospace',
  },
  quotaSection: {
    marginTop: '16px',
    padding: '16px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
  },
  quotaHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  quotaLabel: {
    fontSize: '13px',
    color: '#6b7280',
  },
  progressBar: {
    width: '100%',
    height: '8px',
    backgroundColor: '#e5e7eb',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.3s',
  },
  resetButton: {
    marginTop: '12px',
    padding: '8px 16px',
    fontSize: '13px',
    color: '#dc2626',
    backgroundColor: 'white',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    cursor: 'pointer',
    width: '100%',
  },
  buttonGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  secondaryButton: {
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
    backgroundColor: '#f3f4f6',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid #f3f4f6',
  },
  infoLabel: {
    color: '#6b7280',
    fontSize: '13px',
  },
  infoValue: {
    color: '#1f2937',
    fontSize: '13px',
    fontWeight: 500,
  },
  saveSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '20px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  primaryButton: {
    padding: '12px 32px',
    fontSize: '16px',
    fontWeight: 600,
    color: 'white',
    backgroundColor: '#16a34a',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  unsavedText: {
    fontSize: '14px',
    color: '#ca8a04',
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    width: '400px',
    maxWidth: '90%',
    textAlign: 'center',
  },
  modalTitle: {
    margin: '0 0 12px 0',
    fontSize: '18px',
    fontWeight: 600,
  },
  modalText: {
    margin: '0 0 16px 0',
    color: '#6b7280',
    fontSize: '14px',
  },
  passwordBox: {
    padding: '16px',
    backgroundColor: '#f3f4f6',
    borderRadius: '8px',
    marginBottom: '16px',
  },
  passwordText: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#1f2937',
    letterSpacing: '1px',
  },
  modalButtons: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
  },
  copyButton: {
    flex: 1,
    padding: '12px',
    fontSize: '14px',
    fontWeight: 600,
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  closeButton: {
    flex: 1,
    padding: '12px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#6b7280',
    backgroundColor: '#f3f4f6',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  warningText: {
    margin: 0,
    fontSize: '12px',
    color: '#ca8a04',
  },
  error: {
    padding: '16px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    color: '#dc2626',
  },
  // Upload styles
  fieldsPreview: {
    padding: '12px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    marginBottom: '16px',
  },
  fieldsHeader: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#374151',
    marginBottom: '8px',
  },
  fieldsList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  fieldTag: {
    padding: '4px 8px',
    backgroundColor: '#e5e7eb',
    borderRadius: '4px',
    fontSize: '12px',
    fontFamily: 'monospace',
  },
  fileInput: {
    width: '100%',
    padding: '10px',
    fontSize: '14px',
    border: '1px dashed #d1d5db',
    borderRadius: '6px',
    backgroundColor: '#f9fafb',
    cursor: 'pointer',
  },
  helpText: {
    margin: '8px 0 0 0',
    fontSize: '12px',
    color: '#6b7280',
  },
  selectedFiles: {
    padding: '12px',
    backgroundColor: '#eff6ff',
    borderRadius: '8px',
    marginBottom: '16px',
  },
  fileName: {
    padding: '4px 0',
    fontSize: '13px',
    fontFamily: 'monospace',
    color: '#1f2937',
  },
  uploadResults: {
    marginTop: '16px',
  },
  resultRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    borderRadius: '6px',
    marginBottom: '4px',
    fontSize: '13px',
  },
  resultFilename: {
    fontFamily: 'monospace',
    color: '#374151',
  },
  // Danger Zone styles
  dangerZone: {
    marginTop: '24px',
    marginBottom: '24px',
    padding: '20px',
    backgroundColor: '#FEF2F2',
    border: '1px solid #FCA5A5',
    borderRadius: '8px',
  },
  dangerZoneTitle: {
    margin: '0 0 16px 0',
    fontSize: '14px',
    fontWeight: 600,
    color: '#DC2626',
    textTransform: 'uppercase',
  },
  dangerZoneContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
  },
  dangerZoneText: {
    flex: 1,
  },
  dangerZoneDescription: {
    margin: '4px 0 0 0',
    fontSize: '13px',
    color: '#6b7280',
  },
  dangerZoneWarning: {
    margin: '12px 0 0 0',
    fontSize: '12px',
    color: '#DC2626',
    fontStyle: 'italic',
  },
  dangerButton: {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 600,
    color: 'white',
    backgroundColor: '#DC2626',
    border: 'none',
    borderRadius: '6px',
    whiteSpace: 'nowrap',
  },
  // Delete Modal styles
  deleteModal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    width: '420px',
    maxWidth: '90%',
    textAlign: 'center',
  },
  deleteModalIcon: {
    width: '48px',
    height: '48px',
    margin: '0 auto 16px',
    backgroundColor: '#FEF2F2',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    fontWeight: 700,
    color: '#DC2626',
  },
  deleteModalTitle: {
    margin: '0 0 8px 0',
    fontSize: '18px',
    fontWeight: 600,
    color: '#1f2937',
  },
  deleteModalText: {
    margin: '0',
    color: '#6b7280',
    fontSize: '14px',
  },
  deleteModalEmail: {
    margin: '8px 0 16px 0',
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
  },
  deleteModalWarning: {
    margin: '0 0 20px 0',
    padding: '12px',
    backgroundColor: '#FEF2F2',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#DC2626',
  },
  deleteModalError: {
    margin: '0 0 16px 0',
    padding: '10px',
    backgroundColor: '#FEF2F2',
    border: '1px solid #FCA5A5',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#DC2626',
  },
  deleteModalButtons: {
    display: 'flex',
    gap: '12px',
  },
  cancelButton: {
    flex: 1,
    padding: '12px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
    backgroundColor: '#f3f4f6',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  confirmDeleteButton: {
    flex: 1,
    padding: '12px',
    fontSize: '14px',
    fontWeight: 600,
    color: 'white',
    backgroundColor: '#DC2626',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
};

export default UserDetail;
